<template>
  <component
    v-for="section in visibleSections"
    :is="typeToComponent[section.type]"
    :key="section.key"
    :config="section.settings"
    :data-section-key="preview ? section.key : null"
    :class="{ 'hpr-section--selected': preview && selectedKey === section.key }"
    v-bind="extraProps(section)"
    @click.capture="preview ? onSectionClick(section.key) : null"
  />
</template>

<script setup>
import { computed, onMounted, watch, nextTick } from 'vue';
import { useHomepageConfig } from '@/composables/useHomepageConfig';

import HeroSection from './HeroSection.vue';
import BannerSection from './BannerSection.vue';
import QuickAccessSection from './QuickAccessSection.vue';
import GradesSection from './GradesSection.vue';
import HomeProductsSection from './HomeProductsSection.vue';
import StatsSection from './StatsSection.vue';
import TestimonialsSection from './TestimonialsSection.vue';
import CtaSection from './CtaSection.vue';

const props = defineProps({
  auth: { type: Boolean, default: false },
  user: { type: Object, default: null },
  testimonials: { type: Array, default: () => [] },
  testimonialsLoading: { type: Boolean, default: true },
  preview: { type: Boolean, default: false },
  selectedKey: { type: String, default: null },
});

function onSectionClick(key) {
  window.parent?.postMessage({ source: 'homepage-builder', type: 'section-click', payload: { key } }, '*');
}

const { activeSections, fetchHomepageConfig } = useHomepageConfig();
onMounted(fetchHomepageConfig);

// Scroll the selected section into view when it's chosen from the builder's
// section list (rather than by clicking directly inside the preview).
watch(
  () => props.selectedKey,
  async (key) => {
    if (!key) return;
    await nextTick();
    document.querySelector(`[data-section-key="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },
);

// header/footer are global chrome (App.vue via navbar.vue/footer.vue), not body
// sections; `banners` reuses the existing Website Banners feature/table as-is
// (not present in the moustafa reference — kept because TarekMath's real
// homepage already renders it); `cta` additionally respects its existing
// guest-only business rule regardless of the CMS toggle.
const typeToComponent = {
  hero: HeroSection,
  banners: BannerSection,
  quick_access: QuickAccessSection,
  academic_systems: GradesSection,
  home_products: HomeProductsSection,
  statistics: StatsSection,
  testimonials: TestimonialsSection,
  cta: CtaSection,
};

const visibleSections = computed(() => activeSections().filter((s) => {
  if (!typeToComponent[s.type]) return false;
  if (s.type === 'cta' && props.auth) return false;
  return true;
}));

function extraProps(section) {
  if (section.type === 'hero' || section.type === 'quick_access') {
    return { auth: props.auth, user: props.user };
  }
  if (section.type === 'home_products') {
    return { kind: section.settings?.kind || 'course' };
  }
  if (section.type === 'testimonials') {
    return { testimonials: props.testimonials, loading: props.testimonialsLoading };
  }
  return {};
}
</script>

<style scoped>
.hpr-section--selected {
  outline: 3px solid #2563eb;
  outline-offset: -3px;
  position: relative;
  z-index: 1;
}
</style>
